import { useContext, useMemo } from 'react';
import { IEditMenuAction } from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import {
  FormUpdateAssociatonType,
  getFormUpdateValue,
} from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormValueUtils';
import { mergeBusinessDataMutations } from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/MergeBusinessDataMutations';
import { StandardRutinContext } from '../../../../_StandardRutin/StandardRutinContext';
import { BokforderType__Associations_Rader } from '../Queries/_QueryTypes/BokforderType';
import { UtanordningType__Associations_Rader } from '../Queries/_QueryTypes/UtanordningType';
import {
  IFranvaroUserOption,
  certifyOrRetakeRows,
  findFranvaroForUser,
  getAttestanterWithCurrentUserAsReserv,
  getRowsDeleteObjects,
  hasValidationErrors,
  isAttestButtonDisabled,
  isAttestButtonHidden,
} from '../Utils/BokforderUtils';
import { FranvaroContext } from '../../../../SystemAdministration/ObjektVyer/User/Context/FranvaroContext/FranvaroContext';
import { BokforderWrapperContext } from '../../../Rutiner/Bokforder/Contexts/BokforderWrapperContext';
import { AdditionalAttestDialogOpenEnum } from '../../../Rutiner/Bokforder/Components/BokforderProviderWrapper';
import { TEDEKBokforderTYPEnum } from '../../../../../../globalQueryTypes';

export const useBokforderEditMenuAttestButton = (
  queriesLoading: boolean,
  userID: string | null
): IEditMenuAction => {
  const standardRutinContext = useContext(StandardRutinContext);
  const franvaroContext = useContext(FranvaroContext);
  const bokforderWrapperContext = useContext(BokforderWrapperContext);

  const setAdditionalAttestDialogOpen =
    bokforderWrapperContext.setAdditionalAttestDialogOpen;

  return useMemo(
    () => ({
      id: 'attestButton',
      isDisabled: params =>
        queriesLoading ||
        franvaroContext.franvaroReservForQueryLoading ||
        hasValidationErrors(params) ||
        isAttestButtonDisabled(
          userID ?? '',
          franvaroContext.franvaroReservForData
        )(params),
      isHidden: isAttestButtonHidden(
        userID ?? '',
        franvaroContext.franvaroReservForData
      ),
      label: 'Attestera',
      shortLabel: 'Attestera',
      icon: 'check_circle_outline',
      hideIconIfNotFloating: true,
      onClick: params => {
        const rows:
          | BokforderType__Associations_Rader[]
          | UtanordningType__Associations_Rader[] =
          params.businessDataFormContext.formState.currentValues?._Associations
            ?.Rader ?? [];

        const attestRows: BokforderType__Associations_Rader[] =
          rows?.filter(
            (row: any) => row?._Associations?.Attestant?.id === userID
          ) ?? [];

        const isAttestant = attestRows.length > 0;

        const haveAttesterat = attestRows.every(
          row => row.ATTESTERAD_DATUM != null
        );

        const everyAttestantHasAttesterat = rows.every(
          row => row.ATTESTERAD_DATUM != null
        );

        const currentAttestanterWithCurrentUserAsReserv: IFranvaroUserOption[] =
          getAttestanterWithCurrentUserAsReserv(
            params.businessDataFormContext?.formState.currentValues
              ?._Associations?.Rader ?? [],
            franvaroContext.franvaroReservForData,
            params.businessDataFormContext?.data?.TYP
          );

        const notAttesteradeAttestanter =
          currentAttestanterWithCurrentUserAsReserv.filter(
            user => !user.Attesterat
          );

        const todayDate = new Date();
        const isKontrollattestant =
          params.businessDataFormContext.formState.currentValues?._Associations
            ?.Kontrollattestant?.id === userID;

        const kontrollattestantWithCurrentUserAsReserv =
          franvaroContext.franvaroReservForData.find(franvaro =>
            findFranvaroForUser(
              params.businessDataFormContext.formState.currentValues
                ?._Associations?.Kontrollattestant?.id,
              TEDEKBokforderTYPEnum.UTANORDNING,
              todayDate,
              franvaro
            )
          );

        const haveKontrollattesterat =
          params.businessDataFormContext.formState.currentValues
            ?.KONTROLLATTESTERAD_DATUM != null;

        const isReservForKontrollattestant =
          kontrollattestantWithCurrentUserAsReserv != undefined &&
          !haveKontrollattesterat;

        if (
          (notAttesteradeAttestanter.length > 0 &&
            isAttestant &&
            !haveAttesterat) ||
          notAttesteradeAttestanter.length > 1
        ) {
          setAdditionalAttestDialogOpen(
            AdditionalAttestDialogOpenEnum.ATTESTERA
          );
        } else {
          let updateValues: FormUpdateAssociatonType[][] = [];

          //Ta bort tomma rader
          updateValues = updateValues.concat(
            getRowsDeleteObjects(
              params.businessDataFormContext.formState.currentValues,
              true,
              true
            )
          );

          const todayDateString = todayDate.toLocaleDateString('sv-SE');

          if (
            (isKontrollattestant || isReservForKontrollattestant) &&
            everyAttestantHasAttesterat
          ) {
            const kontrollUpdateValues: FormUpdateAssociatonType[] = [
              {
                operation: 'OwnFields',
                values: {
                  KONTROLLATTESTERAD_DATUM: todayDateString,
                },
              },
            ];

            if (isReservForKontrollattestant) {
              const reservForKontrollattestantUpdateValues: FormUpdateAssociatonType =
                {
                  associationName: 'ReservKontrollattestant',
                  operation: 'Ref',
                  id: userID,
                };
              kontrollUpdateValues.push(reservForKontrollattestantUpdateValues);
            }

            updateValues.push(kontrollUpdateValues);
          }

          if (isAttestant && !haveAttesterat) {
            updateValues = updateValues.concat(
              certifyOrRetakeRows(rows, userID ?? '', todayDateString)
            );
          } else if (notAttesteradeAttestanter.length === 1) {
            const currentAttestantWithMeAsReserv = notAttesteradeAttestanter[0];

            updateValues = updateValues.concat(
              certifyOrRetakeRows(
                rows,
                currentAttestantWithMeAsReserv.id ?? '',
                todayDateString,
                userID
              )
            );
          }

          params.businessDataFormContext.saveForm(
            mergeBusinessDataMutations(
              getFormUpdateValue(
                updateValues,
                params.businessDataFormContext.data ?? {}
              ),
              params.businessDataFormContext.formState.values ?? {}
            ),
            () => {
              standardRutinContext.selectNextDetail.current?.(true);
            }
          );
        }
      },
    }),
    [
      franvaroContext.franvaroReservForData,
      franvaroContext.franvaroReservForQueryLoading,
      queriesLoading,
      setAdditionalAttestDialogOpen,
      standardRutinContext.selectNextDetail,
      userID,
    ]
  );
};
